// FE-SCRM-057 — ProposalsCard on Opportunity detail.
//
// Six BE-SCRM-029 endpoints are wired here:
//
//   List   — fetchProposalsList     → table render.
//   Upload — uploadProposal         → ProposalUploadModal.
//   Sent   — markProposalSent       → MarkSentModal. Invalidates
//            [ACTIVITIES_QUERY_KEY, opportunity_id] so the rail picks
//            up the ``proposal_delivered`` SalesActivity the BE appends.
//   Response — markProposalResponse → MarkResponseModal.
//   Download — fetchProposalDownload (mutation) → window.open.
//
// Response-status chip palette matches the spec line: yellow=awaiting,
// blue=viewed, purple=feedback_received, green=accepted, red=rejected.

import { useState } from "react";

import {
  Card, Toast, Table, THead, TBody, Tr, Th, Td,
} from "@/design-system";

import { RowActionsMenu } from "../components";

import { MarkResponseModal } from "./MarkResponseModal";
import { MarkSentModal } from "./MarkSentModal";
import { ProposalUploadModal } from "./ProposalUploadModal";
import {
  useMarkProposalResponseMutation,
  useMarkProposalSentMutation,
  useProposalDownloadMutation,
  useProposalsListQuery,
  useUploadProposalMutation,
} from "./hooks";
import type { Proposal, ProposalResponseStatus } from "./types";
import { RESPONSE_STATUS_LABEL } from "./types";


export interface ProposalsCardProps {
  opportunity: {
    opportunity_id: string;
    prospect_id: string;
  };
}


export function ProposalsCard({ opportunity }: ProposalsCardProps) {
  const { opportunity_id, prospect_id } = opportunity;
  const listQuery = useProposalsListQuery(opportunity_id);
  const upload = useUploadProposalMutation(opportunity_id);
  const markSent = useMarkProposalSentMutation(opportunity_id);
  const markResponse = useMarkProposalResponseMutation(opportunity_id);
  const download = useProposalDownloadMutation();

  const [uploadOpen, setUploadOpen] = useState(false);
  const [sendTarget, setSendTarget] = useState<Proposal | null>(null);
  const [responseTarget, setResponseTarget] = useState<Proposal | null>(null);
  const [toast, setToast] = useState<
    | null
    | { variant: "success" | "error"; title: string; description?: string }
  >(null);

  const rows = listQuery.data?.rows ?? [];

  function onDownload(p: Proposal) {
    download.mutate(p.proposal_id, {
      onSuccess: (res) => {
        if (res.download_url) {
          window.open(res.download_url, "_blank", "noopener,noreferrer");
        } else {
          setToast({
            variant: "error",
            title: "Couldn't fetch a download URL.",
          });
        }
      },
      onError: () => {
        setToast({
          variant: "error",
          title: "Couldn't fetch a download URL.",
        });
      },
    });
  }

  return (
    <>
      <Card
        data-testid="proposals-card"
        className="flex flex-col gap-3 p-4"
      >
        <header className="flex items-center justify-between">
          <h2 className="text-sm font-semibold text-text-primary">
            Proposals
          </h2>
          {rows.length > 0 && (
            <button
              type="button"
              onClick={() => setUploadOpen(true)}
              data-testid="proposals-upload-button"
              className={
                "rounded-md bg-teal px-3 py-1.5 text-sm font-medium "
                + "text-text-inverse hover:opacity-90"
              }
            >
              Upload proposal
            </button>
          )}
        </header>

        {rows.length === 0 ? (
          <EmptyState onUpload={() => setUploadOpen(true)} />
        ) : (
          <ProposalsTable
            rows={rows}
            onMarkSent={(p) => setSendTarget(p)}
            onMarkResponse={(p) => setResponseTarget(p)}
            onDownload={onDownload}
          />
        )}
      </Card>

      {uploadOpen ? (
        <ProposalUploadModal
          opportunityId={opportunity_id}
          prospectId={prospect_id}
          isPending={upload.isPending}
          onSubmit={(input) => {
            upload.mutate(input, {
              onSuccess: () => {
                setUploadOpen(false);
                setToast({
                  variant: "success",
                  title: "Proposal uploaded.",
                });
              },
            });
          }}
          onClose={() => setUploadOpen(false)}
        />
      ) : null}

      {sendTarget ? (
        <MarkSentModal
          proposal={sendTarget}
          prospectId={prospect_id}
          isPending={markSent.isPending}
          onSubmit={(input) => {
            markSent.mutate(
              { proposal_id: sendTarget.proposal_id, input },
              {
                onSuccess: () => {
                  setSendTarget(null);
                  setToast({
                    variant: "success",
                    title: `Marked v${sendTarget.version} as sent.`,
                  });
                },
              },
            );
          }}
          onClose={() => setSendTarget(null)}
        />
      ) : null}

      {responseTarget ? (
        <MarkResponseModal
          proposal={responseTarget}
          isPending={markResponse.isPending}
          onSubmit={(input) => {
            markResponse.mutate(
              { proposal_id: responseTarget.proposal_id, input },
              {
                onSuccess: () => {
                  setResponseTarget(null);
                  setToast({
                    variant: "success",
                    title: `Recorded response for v${responseTarget.version}.`,
                  });
                },
              },
            );
          }}
          onClose={() => setResponseTarget(null)}
        />
      ) : null}

      {toast ? (
        <Toast
          variant={toast.variant}
          title={toast.title}
          description={toast.description}
          onDismiss={() => setToast(null)}
        />
      ) : null}
    </>
  );
}


function EmptyState({ onUpload }: { onUpload: () => void }) {
  return (
    <div
      data-testid="proposals-card-empty"
      className="flex flex-col gap-3"
    >
      <p className="text-xs text-text-secondary">
        No proposals uploaded yet. Upload the PDF proposal you sent to
        the prospect to track recipient, response, and version history
        here.
      </p>
      <div>
        <button
          type="button"
          onClick={onUpload}
          data-testid="proposals-upload-button"
          className={
            "rounded-md bg-teal px-3 py-1.5 text-sm font-medium "
            + "text-text-inverse hover:opacity-90"
          }
        >
          Upload proposal
        </button>
      </div>
    </div>
  );
}


function ProposalsTable({
  rows, onMarkSent, onMarkResponse, onDownload,
}: {
  rows: Proposal[];
  onMarkSent: (p: Proposal) => void;
  onMarkResponse: (p: Proposal) => void;
  onDownload: (p: Proposal) => void;
}) {
  return (
    <Table
      data-testid="proposals-table"
      wrapperClassName="rounded-md border border-border-subtle"
    >
      <THead className="bg-recessed text-text-tertiary">
        <Tr>
          <Th>Version</Th>
          <Th>Uploaded</Th>
          <Th>File</Th>
          <Th>Sent to</Th>
          <Th>Status</Th>
          <Th>Notes</Th>
          <Th className="w-10" />
        </Tr>
      </THead>
      <TBody>
        {rows.map((row) => (
          <Tr
            key={row.proposal_id}
            data-testid={`proposal-row-${row.proposal_id}`}
            className="border-t border-border-subtle"
          >
            <Td className="font-medium text-text-primary">
              v{row.version}
            </Td>
            <Td className="text-text-secondary">
              {row.created_at ? row.created_at.slice(0, 10) : "—"}
            </Td>
            <Td
              className="text-text-primary"
              title={row.filename}
            >
              <span className="block max-w-[16rem] truncate">
                {row.filename}
              </span>
            </Td>
            <Td className="text-text-secondary">
              {row.recipient_contact_id ? "Recipient on file" : "—"}
            </Td>
            <Td>
              <ResponseStatusChip status={row.response_status} />
            </Td>
            <Td className="text-text-secondary">
              {row.notes ? (
                <span className="block max-w-[20rem] truncate" title={row.notes}>
                  {row.notes}
                </span>
              ) : "—"}
            </Td>
            <Td>
              <RowActionsMenu
                items={[
                  { label: "Download", onClick: () => onDownload(row) },
                  { label: "Mark sent", onClick: () => onMarkSent(row) },
                  {
                    label: "Mark response",
                    onClick: () => onMarkResponse(row),
                  },
                ]}
              />
            </Td>
          </Tr>
        ))}
      </TBody>
    </Table>
  );
}


// Five chip styles per spec line:
//   yellow=awaiting, blue=viewed, purple=feedback_received,
//   green=accepted, red=rejected.
const STATUS_CLS: Record<string, string> = {
  awaiting:
    "bg-amber-100 text-amber-800",
  viewed:
    "bg-blue-100 text-blue-800",
  feedback_received:
    "bg-purple-100 text-purple-800",
  accepted:
    "bg-green-100 text-green-800",
  rejected:
    "bg-red-100 text-red-800",
};


function ResponseStatusChip({ status }: { status: string }) {
  const label = RESPONSE_STATUS_LABEL[status as ProposalResponseStatus] ?? status;
  const cls = STATUS_CLS[status] ?? "bg-recessed text-text-tertiary";
  return (
    <span
      data-testid={`proposal-status-${status}`}
      className={
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs "
        + "font-medium " + cls
      }
    >
      {label}
    </span>
  );
}
